import { Node, UITransform, UIOpacity, Component, __private, TransformBit, Scene, CCObject, errorID, NodeEventType, BaseNode } from 'cc';
import { DEBUG, DEV } from 'cc/env';

declare module 'cc' {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    interface Node {
        x: number;
        y: number;
        z: number;
        scaleX: number;
        scaleY: number;
        rotationX: number;
        rotationY: number;
        rotationZ: number;
        transformWidth: number;
        transformHeight: number;
        skewX: number;
        skewY: number;
        transform: UITransform;
        _transform: UITransform;
        /**
         * 存取 UIOpacity 的透明度數值
         * 若節點上不存在 UIOpacity 時，存取此屬性將會自動 addComponent(UIOpacity)，使用上必須注意。
         */
        opacity: number;
        _uiOpacity: UIOpacity;

        scaleScalar: number;
        _scaleScalar: number;

        _delegatee: Node;

        getComponentInParent<T extends Component>(classConstructor: __private._types_globals__Constructor<T>): T | null;
        getComponentsInParent<T extends Component>(classConstructor: __private._types_globals__Constructor<T>): T[];
    }

    // eslint-disable-next-line @typescript-eslint/no-shadow
    enum NodeEventType {
        ACTIVE_CHANGED = 'active-changed',
    }

    // eslint-disable-next-line @typescript-eslint/naming-convention
    interface BaseNode {
        _zIndex: number;
        zIndex: number;
        reorderChildren(): void;
    }
}

Object.defineProperty(Node.EventType, 'ACTIVE_CHANGED', {
    value: 'active-changed',
    writable: false,
    enumerable: true,
    configurable: true,
});

Object.defineProperty(Node.prototype, 'x', {
    configurable: true,
    get: function get(this: Node) {
        return this.position.x;
    },
    set: function set(this: Node, value: number) {
        this.setPosition(value, this.position.y, this.position.z);
    },
});

Object.defineProperty(Node.prototype, 'y', {
    configurable: true,
    get: function get(this: Node) {
        return this.position.y;
    },
    set: function set(this: Node, value: number) {
        this.setPosition(this.position.x, value, this.position.z);
    },
});

Object.defineProperty(Node.prototype, 'z', {
    configurable: true,
    get: function get(this: Node) {
        return this.position.z;
    },
    set: function set(this: Node, value: number) {
        this.setPosition(this.position.x, this.position.y, value);
    },
});

Object.defineProperty(Node.prototype, 'scaleX', {
    configurable: true,
    get: function get(this: Node) {
        return this.scale.x;
    },
    set: function set(this: Node, value: number) {
        this.setScale(value, this.scale.y, this.scale.z);
    },
});

Object.defineProperty(Node.prototype, 'scaleY', {
    configurable: true,
    get: function get(this: Node) {
        return this.scale.y;
    },
    set: function set(this: Node, value: number) {
        this.setScale(this.scale.x, value, this.scale.z);
    },
});

Object.defineProperty(Node.prototype, 'rotationX', {
    configurable: true,
    get: function get(this: Node) {
        return this.eulerAngles.x;
    },
    set: function set(this: Node, value: number) {
        this.setRotationFromEuler(value, this.eulerAngles.y, this.eulerAngles.z);
    },
});

Object.defineProperty(Node.prototype, 'rotationY', {
    configurable: true,
    get: function get(this: Node) {
        return this.eulerAngles.y;
    },
    set: function set(this: Node, value: number) {
        this.setRotationFromEuler(this.eulerAngles.x, value, this.eulerAngles.z);
    },
});

Object.defineProperty(Node.prototype, 'rotationZ', {
    configurable: true,
    get: function get(this: Node) {
        return this.eulerAngles.z;
    },
    set: function set(this: Node, value: number) {
        this.setRotationFromEuler(this.eulerAngles.x, this.eulerAngles.y, value);
    },
});

Object.defineProperty(Node.prototype, '_transform', {
    configurable: true,
    writable: true,
    value: null,
});

Object.defineProperty(Node.prototype, 'transform', {
    configurable: true,
    get: function get(this: Node) {
        if (!this._transform) {
            this._transform = this.getComponent(UITransform);
        }
        return this._transform;
    },
});

Object.defineProperty(Node.prototype, 'transformWidth', {
    configurable: true,
    get: function get(this: Node) {
        return this.transform.width;
    },
    set: function set(this: Node, value: number) {
        this.transform.setContentSize(value, this.transform.height);
    },
});

Object.defineProperty(Node.prototype, 'transformHeight', {
    configurable: true,
    get: function get(this: Node) {
        return this.transform.height;
    },
    set: function set(this: Node, value: number) {
        this.transform.setContentSize(this.transform.width, value);
    },
});

Object.defineProperty(BaseNode.prototype, 'zIndex', {
    configurable: true,
    get: function get(this: BaseNode) {
        return this._zIndex ?? 0;
    },
    set: function set(this: BaseNode, value: number) {
        if (value === this._zIndex) {
            return;
        }
        this._zIndex = value ?? 0;
        this.parent?.reorderChildren();
    },
});

Object.defineProperty(Node.prototype, 'opacity', {
    configurable: true,
    get: function get(this: Node) {
        if (!this._uiOpacity) {
            this._uiOpacity = this.getComponent(UIOpacity);
            if (!this._uiOpacity) {
                this._uiOpacity = this.addComponent(UIOpacity);
            }
        }
        return this._uiOpacity.opacity;
    },
    set: function set(this: Node, value: number) {
        if (!this._uiOpacity) {
            this._uiOpacity = this.getComponent(UIOpacity);
            if (!this._uiOpacity) {
                this._uiOpacity = this.addComponent(UIOpacity);
            }
        }
        this._uiOpacity.opacity = value;
    },
});
Object.defineProperty(Node.prototype, 'scaleScalar', {
    configurable: true,
    get: function get(this: Node) {
        return this._scaleScalar ?? this.scale.x;
    },
    set(this: Node, value: number) {
        if (this._scaleScalar === value) {
            return;
        }
        this._scaleScalar = value;
        this.setScale(value, value, 1);
    },
});

BaseNode.prototype.reorderChildren = function reorderChildren(this: BaseNode): void {
    this._children.sort((a, b) => a.zIndex - b.zIndex);
    this._updateSiblingIndex();
};

Node.prototype.getComponentInParent = function getComponentInParent<T extends Component>(
    this: Node,
    classConstructor: __private._types_globals__Constructor<T>,
): T | null {
    let { parent } = this;
    while (parent) {
        const component = parent.getComponent(classConstructor);
        if (component) {
            return component;
        }
        ({ parent } = parent);
    }
    return null;
};

Node.prototype.getComponentsInParent = function getComponentsInParent<T extends Component>(
    this: Node,
    classConstructor: __private._types_globals__Constructor<T>,
): T[] {
    const components: T[] = [];
    let { parent } = this;
    while (parent) {
        const comps = parent.getComponents(classConstructor);
        if (comps.length > 0) {
            components.push(...comps);
        }
        ({ parent } = parent);
    }
    return components;
};

Object.defineProperty(Node.prototype, 'skewX', {
    configurable: true,
    enumerable: true,
    get() {
        return this._skewX ?? 0;
    },

    set(v: number) {
        this._skewX = v;
        this.invalidateChildren(TransformBit.SCALE);
        this.updateWorldTransform();
    },
});

Object.defineProperty(Node.prototype, 'skewY', {
    configurable: true,
    enumerable: true,
    get() {
        return this._skewY ?? 0;
    },

    set(v: number) {
        this._skewY = v;
        this.invalidateChildren(TransformBit.SCALE);
        this.updateWorldTransform();
    },
});

const originalActiveDescriptor = Object.getOwnPropertyDescriptor(BaseNode.prototype, 'active');
const originalActiveSetter = originalActiveDescriptor.set;
originalActiveDescriptor.set = function set(this: Node, v: boolean) {
    const activeChanged = this._active !== v;
    originalActiveSetter.call(this, v);
    if (activeChanged) {
        this.emit(NodeEventType.ACTIVE_CHANGED, this);
    }
};
Object.defineProperty(BaseNode.prototype, 'active', originalActiveDescriptor);

const PI180 = Math.PI / 180;
const oldUpdateWorldTransform = Node.prototype.updateWorldTransform;
Node.prototype.updateWorldTransform = function updateWorldTransform(this: Node) {
    if (this.skewX || this.skewY) {
        const skewX = Math.tan(this.skewX * PI180);
        const skewY = Math.tan(this.skewY * PI180);

        this.invalidateChildren(TransformBit.SCALE);
        oldUpdateWorldTransform.call(this);

        const m = this._mat;
        const a = m.m00;
        const b = m.m01;
        const c = m.m04;
        const d = m.m05;
        m.m00 = a + c * skewY;
        m.m01 = b + d * skewY;
        m.m04 = c + a * skewX;
        m.m05 = d + b * skewX;

        this.children.forEach((child) => {
            // 同步影響子節點
            child.invalidateChildren(TransformBit.SCALE | TransformBit.ROTATION);
        });
    } else {
        oldUpdateWorldTransform.call(this);
    }
};

BaseNode.prototype.setParent = function setParent(this: BaseNode, value: BaseNode | Scene | null, keepWorldTransform = false) {
    if (this._parent === value) {
        return;
    }
    const oldParent = this._parent;
    const newParent = value as BaseNode;
    if (
        DEBUG &&
        oldParent &&
        // Change parent when old parent deactivating or activating
        oldParent._objFlags & CCObject.Flags.Deactivating
    ) {
        errorID(3821);
    }

    this._parent = newParent;
    // Reset sibling index
    this._siblingIndex = 0;

    this._onSetParent(oldParent, keepWorldTransform);

    if (this.emit) {
        this.emit(NodeEventType.PARENT_CHANGED, oldParent);
    }

    if (oldParent) {
        if (!(oldParent._objFlags & CCObject.Flags.Destroying)) {
            const removeAt = oldParent._children.indexOf(this);
            if (DEV && removeAt < 0) {
                errorID(1633);
                return;
            }
            oldParent._children.splice(removeAt, 1);
            oldParent._updateSiblingIndex();
            if (oldParent.emit) {
                oldParent.emit(NodeEventType.CHILD_REMOVED, this);
            }
        }
    }

    if (newParent) {
        if (DEBUG && newParent._objFlags & CCObject.Flags.Deactivating) {
            errorID(3821);
        }
        const targetIndex = newParent._children.findIndex((node) => node.zIndex > this.zIndex);
        if (targetIndex === -1) {
            newParent._children.push(this);
            this._siblingIndex = newParent._children.length - 1;
        } else {
            newParent._children.splice(targetIndex, 0, this);
            newParent._updateSiblingIndex();
        }
        if (newParent.emit) {
            newParent.emit(NodeEventType.CHILD_ADDED, this);
        }
    }

    this._onHierarchyChanged(oldParent);
};
